/**
 * reportPicker — the pieces the candidate panel (variant "D") and the Reports
 * cell's popover (variant "E") share: the Download button with the format in
 * its ▾ menu, the formats' order and wording, the remembered format, the
 * caption style over each group, and the sentences that say why a report or a
 * format cannot be picked (epic
 * `2026-10-22-candidate-reports-four-kinds`, sub-task 03).
 *
 * Its own module so the panel does not import the table cell's module, and so
 * one condition is said in one sentence on both surfaces (/pr-review #1092
 * iter 1).
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import { Button, Spinner } from "@nu-assist/ui";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, Download as DownloadIcon } from "lucide-react";
import { useRef, useState } from "react";

import {
  type CandidateReportFormat,
  defaultReportFormat,
} from "../../../clients/candidateReports.client";

/** The formats' order and wording, in the panel and the Reports cell. */
export const REPORT_FORMATS: readonly {
  format: CandidateReportFormat;
  label: string;
  ext: string;
}[] = [
  { format: "docx", label: "Word", ext: ".docx" },
  { format: "pdf", label: "PDF", ext: ".pdf" },
  { format: "txt", label: "Text", ext: ".txt" },
];

/** The word over each group of report radios. */
export const pickerCaptionClass =
  "text-micro font-semibold uppercase tracking-wide text-text-muted";

/** A first build past its poll budget: nothing is being asked any more. */
export const STALLED_TITLE = "Still preparing — open the candidate and try Refresh from Thrive.";

/** A kind with no stored copy (`none` / `deleted`, or no stored format). */
export const NOT_STORED_TITLE =
  "No stored copy yet. It is stored the next time this search refreshes from Thrive.";

/** A format the selected report has no stored file in, while others exist. */
export const FORMAT_NOT_STORED_REASON = "No stored copy of this report in this format.";

/** The index read has not answered yet (the panel waits for it). */
export const CHECKING_STORED_COPY_REASON = "Checking the stored copy…";

/** "1 referee", "3 referees". */
export function refereesLabel(count: number): string {
  return count === 1 ? "1 referee" : `${count} referees`;
}

/** Where the last picked format is kept, per browser (Jose, 2026-10-27). */
export const REPORT_FORMAT_STORAGE_KEY = "nu_candidate_report_format";

/**
 * The format the reader picked last, in this browser, or null. Storage can be
 * blocked or wiped (a private window, cleared site data): then the default
 * rule applies, exactly as on a first visit.
 */
export function readRememberedReportFormat(): CandidateReportFormat | null {
  try {
    const raw = globalThis.localStorage?.getItem(REPORT_FORMAT_STORAGE_KEY);
    return REPORT_FORMATS.some((f) => f.format === raw) ? (raw as CandidateReportFormat) : null;
  } catch {
    return null;
  }
}

/** Keep `format` as the reader's pick; a refused write is not an error. */
export function rememberReportFormat(format: CandidateReportFormat): void {
  try {
    globalThis.localStorage?.setItem(REPORT_FORMAT_STORAGE_KEY, format);
  } catch {
    // Storage blocked: the pick lasts for this page only.
  }
}

/**
 * The default format: the reader's remembered one when the report is stored
 * in it, else the default rule (`defaultReportFormat`: PDF first).
 */
export function preferredReportFormat(
  stored: readonly CandidateReportFormat[],
  remembered: CandidateReportFormat | null
): CandidateReportFormat | null {
  return remembered && stored.includes(remembered) ? remembered : defaultReportFormat(stored);
}

export interface ReportDownloadButtonProps {
  /** The format Download uses; null when the report has no stored file. */
  format: CandidateReportFormat | null;
  /** The formats the selected report is stored in; the others are disabled. */
  offered: readonly CandidateReportFormat[];
  /** Why a format that is not offered cannot be picked. */
  reason: (format: CandidateReportFormat) => string;
  onFormatChange: (format: CandidateReportFormat) => void;
  /** "Download PDF" (`full`) or "PDF" (`short`, a medium-width panel). */
  size?: "full" | "short";
  canDownload: boolean;
  /** A download of this candidate is running (the panel); the Reports cell's form closes instead. */
  downloading?: boolean;
  /** While `downloading`, the visible text in place of "Download PDF" —
   *  "Updating from Thrive…" while the click waits for a rebuild (#874). */
  busyText?: string;
  /** The selected report's full name, read after the button's visible text. */
  reportName: string;
  /** Why Download cannot run (its native tooltip), when it cannot. */
  title?: string;
  /** Click handler; omit it for a `type="submit"` button inside a form. */
  onDownload?: () => void;
  /** `${testId}` on Download, `${testId}-formats` on ▾. */
  testId: string;
  /** `${formatTestIdPrefix}-${format}` on each format in the ▾ menu. */
  formatTestIdPrefix: string;
}

/**
 * Download with the format inside it (Jared, 2026-10-27: "that works", on the
 * split button): [⬇ Download PDF | ▾]. The ▾ opens Word / PDF / Text; picking
 * one only changes the button ("Download Word"), it does not download — the
 * click on Download does. A format the report is not stored in is disabled,
 * says "not stored" in its own text (read once, as part of its name) and
 * gives the full reason as the native tooltip.
 *
 * It replaced the FORMAT radios, which sat far from the report choice in the
 * panel (Jared, same day: "the format thing is floating out there").
 */
export function ReportDownloadButton({
  format,
  offered,
  reason,
  onFormatChange,
  size = "full",
  canDownload,
  downloading = false,
  busyText,
  reportName,
  title,
  onDownload,
  testId,
  formatTestIdPrefix,
}: ReportDownloadButtonProps) {
  const label = REPORT_FORMATS.find((f) => f.format === format)?.label;
  const spoken = label ? `Download ${label}` : "Download";
  const idleText = size === "short" ? (label ?? "Download") : spoken;
  const text = downloading && busyText ? busyText : idleText;
  // Nothing stored means nothing to pick: the ▾ is off and Download's title
  // says why. (Download itself stays focusable for that title.)
  const menuDisabled = offered.length === 0 || downloading;
  // Inside the candidate panel (an `aria-modal` dialog) the menu renders in
  // the dialog, not at the end of <body>, where a screen reader treats it as
  // outside the modal (/pr-review #1102 iter 1). Found when the menu opens.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  // Controlled, so Escape closes it here: inside the Reports cell's popover
  // the two Radix layers do not share one stack (separate package copies),
  // and the menu's own Escape never sees itself as the top layer.
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <span ref={rootRef} className="inline-flex shrink-0">
      <Button
        type={onDownload ? "button" : "submit"}
        variant="primary"
        data-testid={testId}
        // `aria-disabled`, not `disabled`, so the title saying why shows and a
        // focused button keeps focus while its download runs.
        aria-disabled={!canDownload || undefined}
        aria-busy={downloading || undefined}
        // The visible text first (WCAG 2.5.3, "click Download PDF" by
        // voice), then which report.
        aria-label={`${downloading && busyText ? busyText : spoken}, ${reportName}`}
        title={title}
        onClick={
          onDownload
            ? () => {
                if (canDownload) onDownload();
              }
            : undefined
        }
        // `Button` neutralises hover only under `disabled:`; mirror it.
        className="whitespace-nowrap rounded-r-none aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:hover:bg-accent"
      >
        {downloading ? (
          <Spinner size="sm" label={busyText ?? "Downloading"} />
        ) : (
          <DownloadIcon aria-hidden="true" className="h-3.5 w-3.5" />
        )}
        {text}
      </Button>
      <DropdownMenu.Root
        modal={false}
        open={menuOpen}
        onOpenChange={(open) => {
          if (open) {
            setContainer(rootRef.current?.closest<HTMLElement>('[aria-modal="true"]') ?? null);
          }
          setMenuOpen(open);
        }}
      >
        <DropdownMenu.Trigger asChild disabled={menuDisabled}>
          <Button
            type="button"
            variant="primary"
            data-testid={`${testId}-formats`}
            aria-label={label ? `Format: ${label}` : "Format"}
            className="rounded-l-none border-l border-bg px-2"
          >
            <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal container={container ?? undefined}>
          <DropdownMenu.Content
            align="end"
            sideOffset={4}
            data-testid={`${testId}-format-menu`}
            onKeyDown={(e) => {
              if (e.key === "Escape") setMenuOpen(false);
            }}
            className="z-50 min-w-36 rounded-md border border-border bg-surface py-1 shadow-md"
          >
            <DropdownMenu.RadioGroup
              value={format ?? ""}
              onValueChange={(next) => onFormatChange(next as CandidateReportFormat)}
            >
              {REPORT_FORMATS.map(({ format: f, label: l }) => {
                const available = offered.includes(f);
                const why = available ? undefined : reason(f);
                return (
                  <DropdownMenu.RadioItem
                    key={f}
                    value={f}
                    disabled={!available}
                    // The full sentence on hover; the item's own text says
                    // "not stored", so the reason is visible to everyone
                    // and read once, as part of the name (/pr-review #1102).
                    title={why}
                    data-testid={`${formatTestIdPrefix}-${f}`}
                    className="flex cursor-pointer items-center justify-between gap-4 px-3 py-1.5 text-label text-text outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-surface-raised data-[state=checked]:font-semibold data-[disabled]:opacity-40"
                  >
                    <span>
                      {l}
                      {why ? (
                        <span className="ml-2 text-caption font-normal"> not stored</span>
                      ) : null}
                    </span>
                    <DropdownMenu.ItemIndicator>
                      <Check aria-hidden="true" className="h-3.5 w-3.5 text-accent" />
                    </DropdownMenu.ItemIndicator>
                  </DropdownMenu.RadioItem>
                );
              })}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </span>
  );
}
